import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useFullscreen } from '../../hooks/useFullscreen';
import { useOnline } from '../../hooks/useOnline';

interface Props {
  /** "Name · ID", shown as the last line when the learner is signed in. */
  identity?: string;
}

type MicState = 'unknown' | 'granted' | 'denied' | 'unsupported';

type SoundState = 'idle' | 'playing' | 'asked' | 'heard' | 'unsupported';

const TONE_HZ = [523.25, 659.25];
const TONE_MS = 450;

/** A short two-note tone made in the browser (no audio file), at the volume the candidate set, then "did you hear it?". */
function useSoundCheck(): {
  state: SoundState;
  volume: number;
  setVolume: (v: number) => void;
  play: () => void;
  answer: (heard: boolean) => void;
} {
  const AudioCtx = typeof window === 'undefined' ? undefined : window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  const [state, setState] = useState<SoundState>(AudioCtx ? 'idle' : 'unsupported');
  const [volume, setVolume] = useState(0.7);
  const ctxRef = useRef<AudioContext | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      void ctxRef.current?.close().catch(() => undefined);
    },
    [],
  );

  const play = useCallback(() => {
    if (!AudioCtx) return;
    try {
      const ctx = ctxRef.current ?? new AudioCtx();
      ctxRef.current = ctx;
      void ctx.resume();
      const gain = ctx.createGain();
      gain.gain.value = volume * 0.4;
      gain.connect(ctx.destination);
      TONE_HZ.forEach((hz, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = hz;
        osc.connect(gain);
        osc.start(ctx.currentTime + (i * TONE_MS) / 1000);
        osc.stop(ctx.currentTime + ((i + 1) * TONE_MS) / 1000);
      });
      setState('playing');
      timer.current = setTimeout(() => setState('asked'), TONE_HZ.length * TONE_MS + 100);
    } catch {
      setState('unsupported');
    }
  }, [AudioCtx, volume]);

  const answer = useCallback((heard: boolean) => setState(heard ? 'heard' : 'idle'), []);
  return { state, volume, setVolume, play, answer };
}

/** Microphone permission; reading it never prompts, only `request` does. */
function useMicPermission(): { state: MicState; request: () => Promise<void> } {
  const supported = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  const [state, setState] = useState<MicState>(supported ? 'unknown' : 'unsupported');

  useEffect(() => {
    if (!supported || !navigator.permissions?.query) return undefined;
    let status: PermissionStatus | null = null;
    let cancelled = false;
    const sync = () => {
      if (status) setState(status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'unknown');
    };
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        status = result;
        sync();
        result.addEventListener('change', sync);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      status?.removeEventListener('change', sync);
    };
  }, [supported]);

  const request = useCallback(async () => {
    if (!supported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setState('granted');
    } catch {
      setState('denied');
    }
  }, [supported]);

  return { state, request };
}

function Row({
  ok,
  title,
  detail,
  action,
  last = false,
}: {
  ok: boolean;
  title: string;
  detail: string;
  action?: React.ReactNode;
  last?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li className={cn('flex items-center gap-3.5 border-t border-[#EEF1F8] py-[15px]', last && 'border-b')}>
      <span
        className={cn(
          'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-[15px] font-extrabold',
          ok ? 'bg-[#E6F4EC] text-[#166534]' : 'bg-[#FFF4D6] text-[#92400E]',
        )}
      >
        <span aria-hidden>{ok ? '✓' : '!'}</span>
        <span className="sr-only">{ok ? t('exam.ready.ok') : t('exam.ready.todo')}</span>
      </span>
      <div className="min-w-0 flex-1">
        <b className="text-[14.5px] text-[#10182B]">{title}</b>
        <div className="mt-0.5 text-[12.5px] text-[#646D84]">{detail}</div>
      </div>
      {action}
    </li>
  );
}

const ACTION =
  'shrink-0 rounded-xl border border-[#E6E9F2] bg-white px-3 py-2 text-[13.5px] font-bold text-[#10182B] transition hover:bg-[#F4F6FB] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]';

/** What to check before an IELTS paper: connection, full screen for Reading and Writing, the microphone for Speaking. */
export function ExamReadinessCard({ identity }: Props) {
  const { t } = useTranslation();
  const online = useOnline();
  const fullscreen = useFullscreen();
  const mic = useMicPermission();
  const sound = useSoundCheck();

  return (
    <section aria-labelledby="exam-ready-title" className="rounded-3xl border border-[#E6E9F2] bg-white p-6 sm:p-8">
      <h2 id="exam-ready-title" className="font-display text-[22px] font-extrabold tracking-[-0.025em] text-[#10182B]">
        {t('exam.ready.title')}
      </h2>
      <p className="mb-2.5 mt-1.5 text-[13.5px] text-[#646D84]">{t('exam.ready.subtitle')}</p>
      <ul>
        {sound.state !== 'unsupported' && (
          <Row
            ok={sound.state === 'heard'}
            title={t('exam.ready.sound')}
            detail={sound.state === 'asked' ? t('exam.ready.sound_ask') : t('exam.ready.sound_help')}
            action={
              sound.state === 'asked' ? (
                <span className="flex shrink-0 gap-1.5">
                  <button type="button" onClick={() => sound.answer(true)} className={ACTION}>
                    {t('exam.ready.sound_yes')}
                  </button>
                  <button type="button" onClick={() => sound.answer(false)} className={ACTION}>
                    {t('exam.ready.sound_no')}
                  </button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-2.5">
                  <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.1}
                    value={sound.volume}
                    onChange={(e) => sound.setVolume(Number(e.target.value))}
                    aria-label={t('exam.listening.volume')}
                    className="w-20 accent-[#10182B] max-sm:hidden"
                  />
                  <button type="button" onClick={sound.play} disabled={sound.state === 'playing'} className={`${ACTION} disabled:opacity-50`}>
                    {t('exam.ready.sound_play')}
                  </button>
                </span>
              )
            }
          />
        )}
        <Row
          ok={online}
          title={t('exam.ready.internet')}
          detail={online ? t('exam.ready.internet_ok') : t('exam.ready.internet_off')}
        />
        {fullscreen.supported && (
          <Row
            ok={fullscreen.active}
            title={t('exam.ready.fullscreen')}
            detail={t('exam.ready.fullscreen_help')}
            action={
              !fullscreen.active && (
                <button type="button" onClick={fullscreen.toggle} className={ACTION}>
                  {t('exam.ready.enable')}
                </button>
              )
            }
          />
        )}
        {mic.state !== 'unsupported' && (
          <Row
            ok={mic.state === 'granted'}
            title={t('exam.ready.microphone')}
            detail={mic.state === 'denied' ? t('exam.ready.mic_denied') : t('exam.ready.mic_help')}
            action={
              mic.state !== 'granted' && (
                <button type="button" onClick={() => void mic.request()} className={ACTION}>
                  {t('exam.ready.allow')}
                </button>
              )
            }
          />
        )}
        {identity && <Row ok title={t('exam.ready.identity')} detail={identity} last />}
      </ul>
    </section>
  );
}

export default ExamReadinessCard;
